/**
 * Desktop detection + deep-link handoff utilities.
 *
 * Used by `LaunchPage` to transparently forward launches (PACS button
 * or RIS worklist) to the Ainovis One desktop app (Electron shell) when
 * installed, falling back to the regular web flow otherwise. Technique
 * modeled after Zoom / Slack / Figma: invisible protocol navigation +
 * page-visibility timeout.
 *
 * See docs/plan-desktop-app.md Fase 3.
 */

export const DESKTOP_USER_AGENT_MARK = 'Ainovis-Desktop/';

/**
 * True when the current page is running inside the Ainovis One Electron
 * shell (the main process sets a custom User-Agent suffix). Also double-
 * checks the `window.ainovisDesktop` contextBridge API so a spoofed UA
 * alone is not enough.
 */
export function isInsideElectron(): boolean {
  if (typeof window === 'undefined') return false;
  const hasBridge =
    typeof (window as unknown as { ainovisDesktop?: { isDesktop?: boolean } })
      .ainovisDesktop?.isDesktop === 'boolean';
  const uaMark = typeof navigator !== 'undefined'
    && navigator.userAgent.includes(DESKTOP_USER_AGENT_MARK);
  return hasBridge || uaMark;
}

/**
 * sessionStorage key that marks "we already tried to hand this launch off
 * to the desktop app" — prevents the web fallback and the desktop app from
 * bouncing the same URL back and forth indefinitely.
 *
 * The string value is intentionally kept as `'pacsDesktopAttempted'` so
 * sessions opened before Phase 10 still recognise the flag.
 */
export const LAUNCH_DESKTOP_ATTEMPTED_KEY = 'pacsDesktopAttempted';

/**
 * Loopback URL exposed by the Ainovis One desktop shell. Must match
 * `DESKTOP_HEALTH_PORT` in `desktop/src/healthServer.ts`.
 *
 * Browsers treat `http://127.0.0.1` as a "potentially trustworthy origin",
 * so a fetch from an `https://` page does not trigger mixed-content blocks
 * — except in Safari, which still blocks plain-HTTP loopback from HTTPS
 * pages (see `isSafari()` below).
 */
const DESKTOP_HEALTH_URL = 'http://127.0.0.1:47821/ainovis-desktop/health';
const DESKTOP_LAUNCH_URL = 'http://127.0.0.1:47821/ainovis-desktop/launch';

/**
 * True when running on Safari (desktop or iOS). Safari is the only major
 * browser that still blocks `http://127.0.0.1` fetches from `https://`
 * pages as mixed content, so we cannot rely on the loopback probe and
 * must fall back to the iframe + visibility heuristic.
 */
function isSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  // Chrome/Edge on macOS/iOS also include "Safari" in the UA, so we
  // exclude them explicitly.
  return /Safari/.test(ua) && !/Chrome|Chromium|Edg|CriOS|FxiOS/.test(ua);
}

/**
 * Fast probe: does a fetch against the desktop shell's loopback health
 * server. Resolves `true` only when the server answers HTTP 200 with the
 * expected JSON shape within `timeoutMs`.
 */
export async function isDesktopAppRunning(timeoutMs = 800): Promise<boolean> {
  if (typeof fetch !== 'function') return false;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(DESKTOP_HEALTH_URL, {
      method: 'GET',
      mode: 'cors',
      cache: 'no-store',
      signal: controller.signal,
    });
    if (!res.ok) {
      return false;
    }
    const body = (await res.json()) as { ok?: boolean };
    return body?.ok === true;
  } catch (err) {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

export type DesktopHandoffResult = 'opened' | 'not-installed' | 'skipped';

export interface TryOpenDesktopAppOptions {
  /** Milliseconds to wait before giving up. Default 2000. */
  readonly timeoutMs?: number;
  /**
   * Factory for the current-time reference. Injected for tests.
   */
  readonly now?: () => number;
}

/**
 * Attempt to activate the Ainovis One desktop app via the `ainovis://`
 * scheme. If the OS accepts the handoff, the current tab loses focus
 * (`visibilitychange` → hidden) and we resolve with `'opened'`. Otherwise,
 * after `timeoutMs` we resolve with `'not-installed'` so the caller can
 * continue with the regular web flow.
 *
 * Skips entirely (and returns `'skipped'`) when already inside Electron or
 * when the previous attempt for this session is still flagged.
 */
export function tryOpenDesktopApp(
  route: string,
  query: URLSearchParams,
  options: TryOpenDesktopAppOptions = {},
): Promise<DesktopHandoffResult> {
  return new Promise((resolve) => {
    if (isInsideElectron()) {
      resolve('skipped');
      return;
    }

    try {
      if (sessionStorage.getItem(LAUNCH_DESKTOP_ATTEMPTED_KEY) === '1') {
        resolve('skipped');
        return;
      }
      sessionStorage.setItem(LAUNCH_DESKTOP_ATTEMPTED_KEY, '1');
    } catch {
      // sessionStorage unavailable (private mode edge cases) — continue
      // without loop protection; the 2s timer still limits the blast
      // radius.
    }

    const timeoutMs = options.timeoutMs ?? 2000;
    const now = options.now ?? (() => Date.now());
    let iframe: HTMLIFrameElement | null = null;
    let settled = false;
    let timerId: ReturnType<typeof setTimeout> | null = null;
    let visibilityListener: (() => void) | null = null;
    let pageHideListener: (() => void) | null = null;

    const finish = (result: DesktopHandoffResult) => {
      if (settled) return;
      settled = true;
      if (visibilityListener) {
        document.removeEventListener('visibilitychange', visibilityListener);
      }
      if (pageHideListener) {
        window.removeEventListener('pagehide', pageHideListener);
      }
      if (timerId !== null) clearTimeout(timerId);
      if (iframe && iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
      resolve(result);
    };

    const safeRoute = encodeURIComponent(route);
    const qs = query.toString();
    const deepLink = `ainovis://${safeRoute}${qs ? `?${qs}` : ''}`;

    /**
     * Launches the deep-link via a hidden iframe and arms the
     * visibility-based fallback heuristic. We only attach those
     * listeners HERE — never during the loopback probe — because
     * otherwise spurious focus/visibility changes during page load
     * (e.g. dcm4chee opening a new named window) would falsely
     * resolve `'opened'` and close the tab while the probe is still
     * pending.
     */
    const launchDeepLinkViaIframe = () => {
      const start = now();
      visibilityListener = () => {
        if (document.visibilityState === 'hidden' && now() - start > 250) {
          finish('opened');
        }
      };
      pageHideListener = () => finish('opened');
      document.addEventListener('visibilitychange', visibilityListener);
      window.addEventListener('pagehide', pageHideListener);
      timerId = window.setTimeout(() => finish('not-installed'), timeoutMs);

      try {
        iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.setAttribute('aria-hidden', 'true');
        iframe.src = deepLink;
        document.body.appendChild(iframe);
      } catch {
        finish('not-installed');
      }
    };

    // Probe the desktop shell's loopback health server. The deep link is
    // ONLY dispatched when the probe succeeds — otherwise the browser
    // could surface a "Allow this site to open <app>?" prompt and the
    // user would be stuck. If the app is not running we go straight to
    // the web fallback without touching the protocol.
    //
    // Safari exception: it blocks `http://127.0.0.1` fetches from HTTPS
    // pages as mixed content, so the probe is impossible. Fall back to
    // the iframe + visibilitychange heuristic in that case (the user
    // sees a one-time "Allow this site to open Ainovis One?" prompt and
    // can click "Permitir siempre" to silence it).
    if (isSafari()) {
      launchDeepLinkViaIframe();
      return;
    }

    void isDesktopAppRunning().then(async (running) => {
      if (settled) return;
      if (!running) {
        finish('not-installed');
        return;
      }
      // Zero-prompt path: POST the payload to the loopback launch
      // endpoint instead of invoking the OS `ainovis://` scheme. This
      // avoids the browser's "Allow this site to open <app>?" dialog
      // entirely. The shell forwards the payload to the renderer using
      // exactly the same code path as the deep-link.
      const delivered = await postLaunchToDesktop(route, query);
      if (delivered) {
        finish('opened');
        return;
      }
      // Fallback: if the POST fails for any reason (network glitch,
      // shell version mismatch, etc.) we try the protocol handoff. The
      // browser may show its prompt, but at least the launch is not
      // lost.
      launchDeepLinkViaIframe();
      window.setTimeout(() => finish('opened'), 150);
    });
  });
}

/**
 * POSTs a launch payload to the desktop shell's loopback endpoint. Used
 * as the "zero-prompt" path: when this succeeds the user is never asked
 * by the browser to allow opening a custom protocol.
 */
async function postLaunchToDesktop(
  route: string,
  query: URLSearchParams,
): Promise<boolean> {
  if (typeof fetch !== 'function') return false;
  const queryObject: Record<string, string> = {};
  query.forEach((value, key) => {
    queryObject[key] = value;
  });
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 1500);
  try {
    const res = await fetch(DESKTOP_LAUNCH_URL, {
      method: 'POST',
      mode: 'cors',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ route, query: queryObject }),
      signal: controller.signal,
    });
    if (!res.ok) {
      return false;
    }
    const body = (await res.json()) as { ok?: boolean };
    return body?.ok === true;
  } catch (err) {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Clear the session flag. Useful when the user explicitly chooses
 * "Continue in browser" so future PACS launches retry the desktop handoff.
 */
export function resetDesktopAttemptFlag(): void {
  try {
    sessionStorage.removeItem(LAUNCH_DESKTOP_ATTEMPTED_KEY);
  } catch {
    /* noop */
  }
}
